<template>
  <div class="row middle-align-flex fixed-top header-bar">
    <div class="col-12 col-sm-6 col-lg-8">
      <a :href="byodMainUrl">
        <img class='logo' v-bind:src='logo' :alt='altText'>
      </a>
    </div>
    <div class="d-sm-block col-sm-6 col-lg-4">
      <div class="row header-bar__links">
        <div class="header-bar__links-item" v-bind:class="helpClass" v-if="!help.excluded">
          <a v-bind:style="{color: providerColor}" :href="help.url" target="_blank">{{ help.text || $t('byodHelp') }}</a>
        </div>
        <div class="header-bar__links-item" v-bind:class="requirementsClass" v-if="!requirements.excluded">
          <a v-bind:style="{color: providerColor}" v-bind:href="requirements.url" target="_blank">{{ requirements.text || $t('byodRequirements') }}</a>
        </div>
        <div class="header-bar__links-item" v-bind:class="languageButtonClass" v-if="multipleLanguagesEnabled">
          <VpLanguageSwitcher
            :languages="languages"
            :value="currentLanguage"
            :provider-color="providerColor"
            @input="setLanguage"
          />
        </div>
      </div>
    </div>
    <div class="header-bar__menu" :class="{ 'is-menu-open': openMenu }" @click="openMenu = !openMenu">
      <div class="header-bar__menu-line"></div>
      <div class="header-bar__menu-line"></div>
      <div class="header-bar__menu-line"></div>
    </div>
    <div class="header-bar__mobile-nav" :class="{ 'is-open': openMenu }">
      <div class="header-bar__mobile-links">
        <div class="header-bar__link-item" v-if="!help.excluded">
          <a v-bind:style="{color: providerColor}" :href="help.url" target="_blank">{{ help.text || $t('byodHelp') }}</a>
        </div>
        <div class="header-bar__link-item" v-if="!requirements.excluded">
          <a v-bind:style="{color: providerColor}" v-bind:href="requirements.url" target="_blank">{{ requirements.text || $t('byodRequirements') }}</a>
        </div>
        <div class="header-bar__link-item" v-if="multipleLanguagesEnabled">
          <VpLanguageSwitcher
            :languages="languages"
            :value="currentLanguage"
            :provider-color="providerColor"
            @input="setLanguage"
          />
        </div>
      </div>
    </div>
    <div v-if="openMenu" class="header-bar__dark-overlay"></div>
  </div>
</template>

<script>
import storage from '../services/storage';
import * as configHelper from '../lib/configHelper';

export default {
  name: 'nav-bar',
  props: ['customerConfig', 'selectedLanguage'],
  data() {
    return {
      logo: '',
      altText: '',
      providerColor: '',
      requirements: {},
      help: {},
      byodHelp: '',
      byodRequirements: '',
      byodMainUrl: '',
      multipleLanguagesEnabled: false,
      languages: [],
      openMenu: false,
      currentLanguage: this.selectedLanguage || this.$i18n.locale,
    };
  },
  mounted() {
    // const req = require.context('../assets/logo', true, /^\.\/.*\.png$/);
    const programId = this.$route.params.programId || storage.get('programId');
    const selectedProgram = this.customerConfig.programs.find(program => program.programName === programId);

    // TODO: remove logic supporting backwards compatibility: some commonSettings keys moved to program level in programSettings
    // https://github.com/virtual-peaker/product-vp/issues/3571
    const { alt, url } = selectedProgram.programSettings.find(({ key }) => key === 'byodMain')
      || (this.customerConfig.commonSettings || []).find(({ key }) => key === 'byodMain')
      || { alt: `${this.customerConfig.name} BYO Home`, url: this.customerConfig.byodPath || '/' };
    this.logo = this.customerConfig.logo;
    this.providerColor = this.customerConfig.color;
    this.altText = alt;
    this.byodMainUrl = url;
    this.requirements = selectedProgram.programSettings.find(({ key }) => key === 'byodRequirements')
      || (this.customerConfig.commonSettings || []).find(({ key }) => key === 'byodRequirements')
      || { excluded: true };
    this.help = selectedProgram.programSettings.find(({ key }) => key === 'byodHelp')
      || (this.customerConfig.commonSettings || []).find(({ key }) => key === 'byodHelp')
      || { excluded: true };
    const multipleLanguagesSetting = selectedProgram.programSettings.find(({ key }) => key === 'byodMultipleLanguages')
      || (this.customerConfig.commonSettings || []).find(({ key }) => key === 'byodMultipleLanguages')
      || null;
    if (multipleLanguagesSetting) {
      this.multipleLanguagesEnabled = multipleLanguagesSetting.value;
      this.languages = this.multipleLanguagesEnabled ? multipleLanguagesSetting.languages : [];
    }
  },
  methods: {
    setLanguage(langKey) {
      const supportedLocales = Object.keys(this.$i18n.messages);
      const newLang = langKey;
      // Prevent setting an unsupported language
      if (supportedLocales.includes(langKey)) {
        this.currentLanguage = newLang;
        this.$i18n.locale = newLang;
        if (this.$route.params.programId) {
          // remove last token (we have to assume it is the language indicator) and rejoin
          const programNameTokens = this.$route.params.programId.split('-');
          programNameTokens.pop();
          const programName = programNameTokens.join('-');

          const newProgram = `${programName}-${langKey}`;
          if (this.$route.meta.initialValues) {
            this.$router.push({ path: this.$router.path, params: { programId: newProgram }, query: { ...this.$route.query } });
          } else {
            this.$router.push({ path: `/signup/${newProgram}` });
          }
        }
        this.$emit('updateLanguage', langKey);
      }
    },
  },
  computed: {
    helpClass() {
      if (this.requirements.excluded && !this.multipleLanguagesEnabled) {
        return 'col-12';
      }
      if (!this.requirements.excluded && this.multipleLanguagesEnabled) {
        return 'col-4';
      }
      return 'col-6';
    },
    requirementsClass() {
      if (this.help.excluded && !this.multipleLanguagesEnabled) {
        return 'col-12';
      }
      if (!this.help.excluded && this.multipleLanguagesEnabled) {
        return 'col-4';
      }
      return 'col-6';
    },
    languageButtonClass() {
      if (this.help.excluded && this.requirements.excluded) {
        return 'col-12';
      }
      if (!this.help.excluded && !this.requirements.excluded) {
        return 'col-4';
      }
      return 'col-6';
    },
  },
  watch: {
    selectedLanguage(newVal) {
      this.currentLanguage = newVal;
    },
  },
};
</script>

<style lang="scss">
@import '../styles/styles.scss';
@import '../styles/mixins.scss';

.header-bar {
   $navbar: &;
   height: 70px;
   box-shadow: 0 -1px 4px 0 rgba(123, 123, 123, 0.5);
   background-color: #fff;
   padding: 5px 5% 5px 5%;
   @include Tablet {
    height: 100px
   }
   .logo {
    height: 40px;
    margin-top: 5px;
    @include Tablet {
      margin-top: 0px;
      height: 56px
    }
   }

   &__links {
    display: none !important;
    text-align: right;
    @include Tablet {
      display: flex !important;
    }
    &-item {
      align-self: center;
      a {
        font-size: .75em;
      }
    }

   }
   &__menu {
    transition: all .4s ease-in-out;
    position: absolute;
    overflow: hidden;
    top: 25px;
    right: 40px;
    z-index: 7;
    @include Tablet {
      display: none !important;
    }
    &-line {
      width: 25px;
      height: 2px;
      border-radius: 0.5px;
      margin-bottom: 7px;
      background-color: #4F4F4F;
      transition: all .3s ease-in-out;
    }

    &.is-menu-open {
      transform: rotate(90deg);
      transition: all .4s ease-in-out;
    }
    &.is-menu-open > .header-bar__menu-line:nth-child(1) {
      transform: rotate(-45deg) translate(-10px, 10px);
    }
    &.is-menu-open > .header-bar__menu-line:nth-child(2) {
      opacity: 0;
    }
    &.is-menu-open > .header-bar__menu-line:nth-child(3) {
      transform: rotate(45deg) translate(-3px, -3px);
    }
   }
   // START: MOBILE NAVBAR
   &__mobile-nav {
    background-color: #fff;
    position: absolute;
    top: 0;
    right: 0;
    width: 75%;
    height: 100vh;
    overflow: hidden;
    visibility: hidden;
    transform: translate3d(1000px, 0, 0);
    z-index: 6;
    transition: all .4s ease-in-out;
    &.is-open {
      visibility: visible;
      transform: translate3d(0, 0, 0);
    }
    @include Tablet {
      display: none !important;
    }
    #{ $navbar }__mobile-links {
      height: 100%;
      margin: 0;
      padding: 153px 63px 0 32px;
      #{ $navbar }__link-item {
        border-top: 1px solid #DBDBDB;
        padding: 15px 0;
        width: 100%;
        &:last-child {
           border-bottom: 1px solid #DBDBDB;
        }
        a {
          font-size: 20px;
        }
      }
    }
   }

   &__dark-overlay {
      position: absolute;
      content: '';
      top: 0;
      height: 100vh;
      left: 0;
      right: 0;
      z-index: 1;
      background: rgba(0, 0, 0, 0.5);
      background-blend-mode: multiply;
      z-index: 3;
      transition: all .9s ease-in-out;
      @include Tablet {
        display: none !important;
      }
    }
   // END: MOBILE NAVBAR
}

.middle-align-flex {
  align-items: center;
  justify-content: center;
}

.back-link {
  color: #59c483;
  text-align: right;
}

.with-vp {
  color: $light-gray-text;
  font-size: .75em;
}
</style>
